import type { FocusEvent } from "react";
import { useCallback } from "react";
import { useDropdownControlProps } from "../Dropdown";
import { Input } from "../Input";
import type { IComboBoxTriggerProps } from "./types";
import { useComboBoxTrigger } from "./useComboBoxTrigger";

/**
 * The box positions the panel and takes pointer events. The input holds the
 * `combobox` role, so the Input's clear button is its sibling.
 */
export const ComboBoxSingleTrigger = ({
  ref,
  className: _className,
  ...anchorProps
}: IComboBoxTriggerProps) => {
  const {
    disabled,
    readOnly,
    size,
    placeholder,
    inputId,
    searchValue,
    setSearchValue,
    onClear,
    showClearButton,
    invalid,
    loading,
    displayValue,
    activeDescId,
    onInputKeyDownCapture,
    onInputFocus,
    dataAttributes,
    searchInputRef,
  } = useComboBoxTrigger();
  const controlProps = useDropdownControlProps();

  // The box is focusable only so a click on its padding lands somewhere; pass it on.
  const handleBoxFocus = useCallback(
    (e: FocusEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        searchInputRef.current?.focus();
      }
    },
    [searchInputRef],
  );

  return (
    <div
      {...anchorProps}
      ref={ref}
      className="nuco-combobox__trigger"
      onFocus={handleBoxFocus}
    >
      <Input
        {...controlProps}
        ref={searchInputRef}
        id={inputId}
        {...dataAttributes}
        aria-autocomplete="list"
        aria-activedescendant={activeDescId}
        size={size}
        value={searchValue || displayValue}
        onChange={setSearchValue}
        onFocus={onInputFocus}
        onKeyDownCapture={onInputKeyDownCapture}
        onClear={showClearButton ? onClear : undefined}
        disabled={disabled}
        readOnly={readOnly}
        invalid={invalid}
        loading={loading}
        placeholder={placeholder}
        autoComplete="off"
      />
    </div>
  );
};
